import React, { useState, useEffect, useRef } from 'react';
import {
  ReloadOutlined,
  PlusCircleFilled,
  MinusCircleFilled,
  CloseCircleFilled,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAccessibility } from './AccessibilityContext';
import * as S from './Styled';

const ContrastIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="1em"
    height="1em"
    viewBox="0 0 24 24"
    fill="none"
    style={{ display: 'inline-block', verticalAlign: '-0.125em', fontSize: 18 }}
  >
    <circle cx="12" cy="12" r="12" fill="white" />
    <circle cx="12" cy="12" r="6.25" stroke="#1D1D1B" strokeWidth="1.5" fill="none" />
    <path d="M12 5C15.866 5 19 8.13401 19 12C19 15.866 15.866 19 12 19V5Z" fill="#1D1D1B" />
  </svg>
);


const AccessibilityToolbar = () => {
  const { isToolbarVisible, hideToolbar } = useAccessibility();
  const { t } = useTranslation();

  const physicalVhRef = useRef(null);

  const [settings, setSettings] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('accessibility-settings');
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          return { fontSize: 100, contrast: 'normal' };
        }
      }
    }
    return { fontSize: 100, contrast: 'normal' };
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('accessibility-settings', JSON.stringify(settings));
    }
  }, [settings]);

  useEffect(() => {
    const root = document.documentElement;
    const wrapper = document.getElementById('zoom-wrapper');
    const scale = settings.fontSize / 100;

    root.setAttribute('data-contrast', settings.contrast);

    if (!wrapper) return;

    let panelStyle = document.getElementById('a11y-panel-style');
    if (!panelStyle) {
      panelStyle = document.createElement('style');
      panelStyle.id = 'a11y-panel-style';
      document.head.appendChild(panelStyle);
    }

    if (scale === 1) {
      wrapper.style.removeProperty('zoom');
      root.style.removeProperty('--html-zoom');
      root.style.setProperty('--full-vh', `${window.innerHeight / 100}px`);
      root.style.removeProperty('--full-vw');
      root.style.removeProperty('--zoom-padding');
      root.style.removeProperty('--panel-padding-left');
      root.classList.remove('zoom-lg', 'zoom-sm', 'zoom-md');
      physicalVhRef.current = window.innerHeight;
      panelStyle.textContent = '';
    } else {
      wrapper.style.zoom = String(scale);
      root.style.setProperty('--html-zoom', String(scale));

      if (scale >= 1.4) {
        root.classList.add('zoom-lg');
        root.classList.remove('zoom-sm', 'zoom-md');
        root.style.setProperty('--panel-padding-left', `${(12 / scale).toFixed(2)}px`);
      } else if (scale >= 1.1) {
        root.classList.add('zoom-md');
        root.classList.remove('zoom-lg', 'zoom-sm');
        root.style.setProperty('--panel-padding-left', `${(12 / scale).toFixed(2)}px`);
      } else if (scale < 1) {
        root.classList.add('zoom-sm');
        root.classList.remove('zoom-lg', 'zoom-md');
        root.style.removeProperty('--panel-padding-left');
      } else {
        root.classList.remove('zoom-lg', 'zoom-sm', 'zoom-md');
        root.style.removeProperty('--panel-padding-left');
      }

      if (scale >= 1.4) {
        const gap = (25 / scale).toFixed(2);
        const insGap = (84 / scale).toFixed(2);
        panelStyle.textContent =
          `.header .favorites{display:none!important}` +
          `.header .cart,.header .thunder{margin-right:${gap}px!important}` +
          `.header .insurance{margin-right:${insGap}px!important}` +
          `.header .cart a>div:first-child,.header .thunder a>div:first-child{margin-right:0!important}` +
          `@media(max-width:767px){.header .cart{margin-right:0!important}}`;
      } else {
        panelStyle.textContent = '';
      }

      const baseline = physicalVhRef.current || window.innerHeight;
      root.style.setProperty('--full-vh', `${(baseline / scale / 100).toFixed(4)}px`);
      root.style.setProperty('--full-vw', `${(window.innerWidth / scale / 100).toFixed(4)}px`);
      root.style.setProperty('--zoom-padding', `${(24 / scale).toFixed(4)}px`);
    }

    const raf = requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });

    return () => {
      cancelAnimationFrame(raf);
    };
  }, [settings.fontSize, settings.contrast]);

  useEffect(() => {
    const scale = settings.fontSize / 100;
    if (scale === 1 || typeof window === 'undefined') return;

    const root = document.documentElement;
    const handleResize = () => {
      root.style.setProperty('--full-vw', `${(window.innerWidth / scale / 100).toFixed(4)}px`);
      root.style.setProperty('--full-vh', `${(window.innerHeight / scale / 100).toFixed(4)}px`);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [settings.fontSize]);

  const handleReset = () => setSettings({ fontSize: 100, contrast: 'normal' });

  const handleContrastToggle = () =>
    setSettings(prev => ({
      ...prev,
      contrast: prev.contrast === 'normal' ? 'dark' : 'normal',
    }));

  const handleFontSizeIncrease = () =>
    setSettings(prev => ({ ...prev, fontSize: Math.min(prev.fontSize + 10, 150) }));

  const handleFontSizeDecrease = () =>
    setSettings(prev => ({ ...prev, fontSize: Math.max(prev.fontSize - 10, 80) }));

  return (
    <S.Toolbar className="accessibility-toolbar" $visible={isToolbarVisible}>
      <S.Container>
        <S.Content>
          <S.ResetBtn type="text" onClick={handleReset}>
            {t('accessibilityToolbar.reset')} <ReloadOutlined />
          </S.ResetBtn>

          <S.ContrastBtn type="text" onClick={handleContrastToggle}>
            {t('accessibilityToolbar.contrast')} <ContrastIcon />
          </S.ContrastBtn>

          <S.SizeGroup>
            <S.SizeLabel>{t('accessibilityToolbar.size')}</S.SizeLabel>
            <S.SizeButtonGroup>
              <S.SizeBtn
                type="text"
                onClick={handleFontSizeIncrease}
                disabled={settings.fontSize >= 150}
                title={t('accessibilityToolbar.increase_font')}
              >
                <PlusCircleFilled />
              </S.SizeBtn>
              <S.SizeBtn
                type="text"
                onClick={handleFontSizeDecrease}
                disabled={settings.fontSize <= 80}
                title={t('accessibilityToolbar.decrease_font')}
              >
                <MinusCircleFilled />
              </S.SizeBtn>
            </S.SizeButtonGroup>
          </S.SizeGroup>

          <S.CloseBtn type="text" onClick={hideToolbar}>
            {t('accessibilityToolbar.hide_settings')} <CloseCircleFilled />
          </S.CloseBtn>
        </S.Content>
      </S.Container>
    </S.Toolbar>
  );
};

export default AccessibilityToolbar;
